import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { object, array, number } from 'yup'
import { withFormik } from 'formik'

import SupportPopup from '../Popups/SupportPopup'
import { getAssetLink } from '../../helpers/get-asset-link'
import api from '../../api/api'

import BookingForm from './BookingForm'
import { calculateTotalPrice } from './helpers'

const BookingSidebar = (props) => {
  const {
    serviceBody,
    servicesFaqLink,
    serviceQuestions,
    handleChange,
    values,
    errors,
    touched,
    handleSubmit,
    setFieldTouched,
  } = props
  const { t } = useTranslation(['booking'])
  const [visibleSupportPopup, setVisibleSupportPopup] = useState(false)

  const onAnswerPress = (question, answer) => {
    if (typeof values[question.customId] !== 'number') {
      setFieldTouched(question.customId)
      const value = values[question.customId]
      const isSelected =
        value.answers.find(({ customId }) => customId === answer.customId) !== undefined

      if (question.selectType === 'single') {
        if (answer.pricingType === 'discount') {
          handleChange({
            target: {
              name: question.customId,
              value: {
                ...value,
                costs: [],
                discount: isSelected
                  ? 0
                  : !Number.isNaN(Number(answer.pricingValue))
                    ? parseInt(answer.pricingValue)
                    : answer.pricingValue,
                answers: isSelected ? [] : [{ customId: answer.customId, value: answer.title }],
              },
            },
          })
        } else {
          handleChange({
            target: {
              name: question.customId,
              value: {
                ...value,
                costs: isSelected
                  ? values[question.customId].costs.filter(
                      ({ customId }) => customId !== answer.customId,
                    )
                  : [
                      {
                        customId: answer.customId,
                        value: !Number.isNaN(Number(answer.pricingValue))
                          ? parseInt(answer.pricingValue)
                          : answer.pricingValue,
                      },
                    ],
                answers: isSelected ? [] : [{ customId: answer.customId, value: answer.title }],
                discount: 0,
              },
            },
          })
        }
      }

      if (question.selectType === 'multiple') {
        handleChange({
          target: {
            name: question.customId,
            value: {
              ...value,
              answers: isSelected
                ? value.answers.filter(({ customId }) => customId !== answer.customId)
                : [...value.answers, { customId: answer.customId, value: answer.title }],
              costs:
                answer.pricingType !== 'discount'
                  ? isSelected
                    ? value.costs.filter(({ customId }) => customId !== answer.customId)
                    : [
                        ...value.costs,
                        {
                          customId: answer.customId,
                          value: !Number.isNaN(Number(answer.pricingValue))
                            ? parseInt(answer.pricingValue)
                            : answer.pricingValue,
                        },
                      ]
                  : values[question.customId].costs,
              discount:
                answer.pricingType === 'discount'
                  ? isSelected
                    ? value.discount - Number(answer.pricingValue)
                    : value.discount + Number(answer.pricingValue)
                  : value.discount,
            },
          },
        })
      }
    }
  }

  const onInputChange = (question, answer, value) => {
    const prevValue = values[question.customId]
    handleChange({
      target: {
        name: question.customId,
        value: { ...prevValue, answers: [{ customId: answer.customId, value }] },
      },
    })
  }

  return (
    <div className="booking-sidebar-container">
      <div className="booking-service-detials-sections">
        <h3>{serviceBody.name}</h3>
        <div className="service-image">
          <img
            src={getAssetLink({ path: 'abc', file: serviceBody.image })}
            alt={t('booking:sidebar.serviceImageAlt')}
          />
          <div className={`info-buttons ${serviceBody.image && 'with-image'}`}>
            {servicesFaqLink ? (
              <button
                className="button button-primary"
                onClick={() => window.open(servicesFaqLink, '_blank')}
              >
                {t('booking:sidebar.faq')}
              </button>
            ) : null}
            <button className="button button-primary" onClick={() => setVisibleSupportPopup(true)}>
              {t('booking:sidebar.contactSupport')}
            </button>
          </div>
        </div>
        <div className="service-description-container">
          <h4>{t('booking:sidebar.descriptionTitle')}</h4>
          <div>{serviceBody.description}</div>
        </div>
        {serviceQuestions && (
          <div className="servise-questions-wrapper">
            <BookingForm
              questions={serviceQuestions}
              onAnswerPress={onAnswerPress}
              onInputChange={onInputChange}
              values={values}
              errors={errors}
              touched={touched}
            />
          </div>
        )}
      </div>
      <div className="service-footer">
        <div className="price">
          ${serviceBody.fixedPrice ? `${serviceBody.fixedPrice}/hr` : calculateTotalPrice(values)}
        </div>
        <div onClick={() => handleSubmit()} className={`button button-primary`}>
          {t('booking:sidebar.requestService')}
        </div>
      </div>
      <SupportPopup
        isOpen={visibleSupportPopup}
        cancelCallback={() => setVisibleSupportPopup(false)}
      />
    </div>
  )
}

BookingSidebar.propTypes = {
  serviceBody: PropTypes.object,
  servicesFaqLink: PropTypes.string,
  serviceQuestions: PropTypes.array,
  handleChange: PropTypes.func,
  setFieldTouched: PropTypes.func,
  values: PropTypes.object,
  errors: PropTypes.object,
  handleSubmit: PropTypes.func,
  touched: PropTypes.object,
  closeSidebar: PropTypes.func,
}

export const mapPropsToValues = (props) => {
  const values = { totalPrice: 0 }
  props.serviceQuestions.map(
    ({ customId }) =>
      (values[customId] = {
        costs: [],
        answers: [],
        discount: 0,
      }),
  )
  return values
}

export default withFormik({
  enableReinitialize: true,
  mapPropsToValues,
  handleSubmit: async (values, { props, setSubmitting }) => {
    const questions = props.serviceQuestions
      .filter(({ customId }) => !customId.includes('image'))
      .map(({ customId, title }) => ({
        question: title,
        answers: values[customId].answers.map(({ value }) => {
          if (value instanceof Date) {
            let hoursDiff = value.getHours() - value.getTimezoneOffset() / 60
            value.setHours(hoursDiff)
            return value
          }
          return value
        }),
      }))

    const images = props.serviceQuestions
      .filter(({ answers }) => answers.find((answer) => answer.type === 'image'))
      .map(({ customId, title }) => ({
        question: title,
        answers: values[customId].answers.map(({ value }) => value),
      }))

    const imageGallery = []

    if (images && images.length > 0 && images[0].answers[0]) {
      for (let i = 0; i < images.length; i += 1) {
        if (images[i].answers[0]) {
          imageGallery.push(...images[i].answers[0])
        }
      }
    }

    const finalPrice = calculateTotalPrice(values)

    const data = {
      questions: JSON.stringify(questions),
      name: props.serviceBody.name,
    }

    if (typeof finalPrice === 'string') {
      data.customPrice = finalPrice
      data.price = 0
    } else {
      data.price = props.serviceBody.fixedPrice ? props.serviceBody.fixedPrice : finalPrice
    }

    if (imageGallery && imageGallery.length > 0) {
      imageGallery.forEach((image, i) => {
        data[`image${i + 1}`] = image.file
      })
    }

    try {
      await api.bookService(data)
      props.closeSidebar()
    } catch {
      setSubmitting(false)
    }
  },
  validationSchema: (props) => {
    const validationFields = Object.assign(
      {},
      ...props.serviceQuestions.map(({ customId, isRequired }) => ({
        [customId]: object()
          .shape({
            costs: array().notRequired(),
            answers: isRequired ? array().min(1).required() : array().notRequired(),
            discount: number().required(),
          })
          .required(),
      })),
    )

    return object().shape(validationFields)
  },
})(BookingSidebar)
